import { Divider, Select, SelectProps, Skeleton, Spin } from 'antd';
import React, { forwardRef, Suspense, useContext, useEffect } from 'react';
import { Environment } from 'relay-runtime';

import useDispatchSetterStore from '../../hooks/useDispatchSetterStore';
import useDynamicSelector from '../../hooks/useDynamicSelector';
import { SmxPropsContext } from '../../providers/propsProvider';
import { getChangeUpdateFetchKey, getSetterListItem, IListItemState } from '../../store/slices/listItemSlice';
import { extraFieldsType, ListItemOptionType, ListItemType } from '../../types';
import CommonLoaderListItem, { ParentRefType } from './CommonLoaderListItem';
import ListItemForm from './listItemForm';
import ListItemMessageError from './listItemMessageError';
import ListItemOne from './listItemOne';
import ListItemOption from './listItemOption';
import useChildParentHandler from './useChildParentHandler';

export interface IListItem extends SelectProps<any> {
    initItems?: ListItemType[];
    // name list of item that should show
    nameList: string;
    // reference to external component to control:
    // - child list for list item
    // - dynamic list for sub list by linkId
    parentRef?: React.RefObject<ParentRefType>;
    parent?: string;
    readonly?: boolean;
    onUpdateList?: () => void;
    indexUpdate?: number;
    extraFields?: extraFieldsType[];
    environment?: Environment;
    // need to use with DynamicList as parentRef
    parentNameList?: string;
    group?: boolean;
}

const ListItem: React.FC<IListItem> = forwardRef<any, Omit<IListItem, 'nameList'>>(
    (
        {
            parent: _,
            initItems,
            parentRef,
            readonly,
            onUpdateList,
            indexUpdate: __,
            parentNameList,
            group,
            ...restProps
        },
        ref,
    ) => {
        const { nameList, prefixStore } = useContext(SmxPropsContext);
        const setterListItem = useDispatchSetterStore<IListItemState>(getSetterListItem(nameList + prefixStore));
        const changeUpdateFetchKey = useDispatchSetterStore<IListItemState>(
            getChangeUpdateFetchKey(nameList + prefixStore),
        );
        const originalItems = useDynamicSelector('listItem', 'originalItems');
        const items = useDynamicSelector('listItem', 'items');
        const currentItemId = useDynamicSelector('listItem', 'currentItemId');
        const parentItem = useDynamicSelector('listItem', 'parentItem');
        const parentDynamicItem = useDynamicSelector('listItem', 'parentDynamicItem');
        const childIdEdit = useDynamicSelector('listItem', 'childIdEdit');
        const spinner = useDynamicSelector('listItem', 'spinner');
        const editMode = useDynamicSelector('listItem', 'editMode');
        const connectionId = useDynamicSelector('listItem', 'connectionId');

        if (group) readonly = true;

        useChildParentHandler(parentRef);

        useEffect(() => {
            const interval = setInterval(() => {
                if (!parentRef?.current || !parentRef.current.hasOwnProperty('getCurrentItem')) {
                    return;
                }

                const newParentItem = parentRef.current?.getCurrentItem();

                if (parentNameList) {
                    if (
                        JSON.stringify(parentDynamicItem) !== JSON.stringify(newParentItem) &&
                        (parentDynamicItem || newParentItem)
                    ) {
                        setterListItem({ parentDynamicItem: newParentItem });
                    }
                } else {
                    if (JSON.stringify(parentItem) !== JSON.stringify(newParentItem) && (parentItem || newParentItem)) {
                        setterListItem({ parentItem: newParentItem });
                    }
                }
            }, 400);

            return () => {
                clearInterval(interval);
            };
        }, [parentItem, parentDynamicItem]);

        useEffect(() => {
            if (childIdEdit < 0 || (parentItem && parentItem.child?.split(',').includes(`${childIdEdit}`))) {
                if (editMode === 'deleting') {
                    setterListItem({ editMode: 'viewing' });
                }
            }
        }, [items]);

        useEffect(() => {
            if (initItems) {
                setterListItem({ typeList: 'local' });
                setterListItem({ originalItems: initItems });
            } else {
                setterListItem({ typeList: 'remote' });
            }
        }, [initItems]);

        useEffect(() => {
            onUpdateList && onUpdateList();
        }, [originalItems]);

        const onChange = (value: number) => {
            setterListItem({
                currentItemId: value,
                currentItem: items.find((item) => value === item.id),
            });
        };

        const customFilterOption = (inputValue: string, option: any) => {
            return option.title.toLowerCase().includes(inputValue.toLowerCase());
        };

        const popupRender = (menu: React.ReactElement) => (
            <Spin tip="Loading" size="small" spinning={spinner}>
                <ListItemMessageError />
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <ListItemForm connectionId={connectionId} parentRef={parentRef} />
            </Spin>
        );

        const renderOption = (item: ListItemType): ListItemOptionType => {
            return {
                label: <ListItemOption item={item} readonly={readonly} connectionId={connectionId} />,
                title: `${item.name}${item.code ? ' (' + item.code + ')' : ''}`,
                value: item.id,
            };
        };

        const renderAllOptions = () => {
            if (!group) return items.map(renderOption);

            const groups: { [key: string]: ListItemType[] } = {};
            items.forEach((item) => {
                groups[item.parent] = groups[item.parent] || [];
                groups[item.parent].push(item);
            });

            return Object.entries(groups).map(([key, value]) => {
                return {
                    label: (
                        <Suspense fallback={<Skeleton.Button active size={'small'} shape={'default'} block />}>
                            <ListItemOne itemId={key} />
                        </Suspense>
                    ),
                    title: key,
                    options: value.map(renderOption),
                };
            });
        };

        const renderLabel = ({ title }: any) => {
            return <span>{title}</span>;
        };

        const onDropdownVisibleChange = (visible: boolean) => {
            if (visible) {
                changeUpdateFetchKey({});
            }

            setterListItem({
                editMode: 'viewing',
                currentEditItem: null,
                // spinner: false
            });
        };

        return (
            <>
                <CommonLoaderListItem parentNameList={parentNameList} subRef={ref} />
                <Select
                    key={parentItem?.id}
                    allowClear
                    style={{ width: '100%' }}
                    disabled={!!parentRef?.current && !parentItem && !parentDynamicItem}
                    placeholder="select option ..."
                    popupRender={readonly ? undefined : popupRender}
                    labelRender={renderLabel}
                    filterOption={customFilterOption}
                    options={renderAllOptions()}
                    value={currentItemId}
                    onChange={onChange}
                    loading={spinner}
                    onOpenChange={onDropdownVisibleChange}
                    showSearch
                    {...restProps}
                />
            </>
        );
    },
);

export default ListItem;
